import type { CommandEventType } from './events'

/**
 * UI metadata for /commands event triggers.
 *
 * `wired: true` means the platform actually emits this event today and an
 * event-triggered command will fire. `wired: false` means the event type
 * is declared but no producer code emits it yet — the form surfaces these
 * as "Coming soon" so users don't configure commands that silently never
 * run.
 *
 * Keep this in sync with the actual `emitCommandEvent()` call sites in
 * the codebase. Audit pattern: `grep -rn "emitCommandEvent\\(" --include="*.ts"`.
 */
export const EVENT_TYPE_META: Record<CommandEventType, { label: string; wired: boolean; description?: string }> = {
  'campaign.launched':    { label: 'Campaign was launched',         wired: true,  description: 'Fires when a campaign transitions to active for the first time.' },
  'campaign.paused':      { label: 'Campaign was paused',           wired: true,  description: 'Fires when a campaign is paused from the campaign actions menu or chat.' },
  'budget.threshold_hit': { label: 'Campaign hit budget threshold', wired: true,  description: 'Fires once per 24h per campaign when spend crosses the workspace budget warning percentage.' },
  'creative.approved':    { label: 'Creative was approved',         wired: true,  description: 'Fires when you approve an AI-generated video creative from the review screen.' },
  'creative.rejected':    { label: 'Creative was rejected',         wired: true,  description: 'Fires when you reject an AI-generated video creative from the review screen.' },
  'pacing.underpacing':   { label: 'Campaign is underpacing',       wired: true,  description: 'Fires once per 24h per campaign when spend is more than 15% behind the expected pace based on the campaign\u2019s flight dates.' },
  'report.weekly_ready':  { label: 'Weekly report is ready',        wired: true,  description: 'Fires once per workspace each Monday morning when the weekly performance digest cron runs.' },
}

/** Backward-compat alias used by `propose_command_form_patch` and form UI. */
export const EVENT_TYPE_LABELS: Record<CommandEventType, string> = Object.fromEntries(
  Object.entries(EVENT_TYPE_META).map(([key, meta]) => [key, meta.label]),
) as Record<CommandEventType, string>

/** Event types that the platform actually emits today. */
export const WIRED_EVENT_TYPES: Set<CommandEventType> = new Set(
  (Object.entries(EVENT_TYPE_META) as Array<[CommandEventType, { wired: boolean }]>)
    .filter(([, meta]) => meta.wired)
    .map(([key]) => key),
)

export function isWiredEventType(eventType: string): boolean {
  return WIRED_EVENT_TYPES.has(eventType as CommandEventType)
}
